import { Component, inject, OnInit, OnDestroy, signal } from '@angular/core';
import { CsIconModule } from '@components/icon';
import { Catalog, Regimen, ProductCollection, ProductCollectionDisplayTypeEnum, Experiment } from '@cureskin/api-client/src/server-api';
import SwiperCore, { Scrollbar, Autoplay, Pagination, SwiperOptions } from 'swiper';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BroadcastService } from '@services/broadcast-service';
import { CartService } from '@services/cart-service/cart.service';
import { ConnectionService } from '@services/connection-service';
import { CurrentComponentService } from '@services/current-component';
import { RegimenService } from '@services/regimen-service';
import { ShopService } from '@services/shop-service/shop.service';
import { AppConfig } from 'client/app/app.config';
import { fromActions } from '@store/actions';
import { fromSelectors } from '@store/selectors';
import { Subscription } from 'rxjs';
import { CartUpdateOptions } from '@shared/types/cart.types';
import { RegimenProductsListComponent } from '../components/regimen-products-list/regimen-products-list.component';
import { AcqShopExperiment, ExperimentService } from '@services/experiment';
import { InstantCheckupService } from '@services/instant-checkup-service/instant-checkup.service';
import { ProductCollectionComponent } from '@components/product-collection/product-collection.component';
import { ProductCollectionJumboComponent } from '@components/product-collection-jumbo/product-collection-jumbo.component';
import { ProductCollectionBannerComponent } from '@components/product-collection-banner/product-collection-banner.component';
import { LoadingModule } from '@components/loading';
import { SwiperModule } from 'swiper/angular';
import { CommonModule } from '@angular/common';
import { BottomNavComponent } from '@shared/bottom-nav/bottom-nav.component';
import { CartDetailStripModule } from '@shared/cart-detail-strip/cart-detail-strip.module';
import { DirectivesModule } from '@directives/directives.module';
import { ExploreService } from '@services/explore-service/explore.service';
import { EventLoggerService } from '@services/event-logger-service';
import { ProductHorizontalComponent } from '../components/product-horizontal/product-horizontal.component';
import { ProductComponent } from '../components/product/product.component';
import { LockedProductBottomsheetComponent, LockedProductBottomsheetData, LockedProductType } from '@shared/bottom-sheet-layouts/locked-product-bottomsheet/locked-product-bottomsheet.component';
import { RegimenClass } from './unpaid-user-shop.types';
import { extractRegimenProducts } from 'client/helper/regimen-helper';
import { getProductImage } from 'client/helper/product-helper';

SwiperCore.use([Autoplay, Scrollbar, Pagination]);

@Component({
  selector: 'app-unpaid-user-shop',
  imports: [
    BottomNavComponent,
    CartDetailStripModule,
    CommonModule,
    CsIconModule,
    DirectivesModule,
    LoadingModule,
    SwiperModule,
    RegimenProductsListComponent,
    ProductCollectionComponent,
    ProductComponent,
    ProductCollectionBannerComponent,
    ProductCollectionJumboComponent,
    ProductHorizontalComponent,
  ],
  templateUrl: './unpaid-user-shop.component.html',
  host: { class: 'tw-block tw-h-full tw-w-full tw-overflow-y-auto tw-overflow-x-hidden' },
})
export class UnpaidUserShopComponent implements OnInit, OnDestroy {

  private readonly appConfig: AppConfig = inject(AppConfig);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly broadcast = inject(BroadcastService);
  private readonly cartService = inject(CartService);
  private readonly connectionService = inject(ConnectionService);
  private readonly currentComponentService = inject(CurrentComponentService);
  private readonly router = inject(Router);
  private readonly eventLoggerService = inject(EventLoggerService);
  private readonly experimentService = inject(ExperimentService);
  private readonly exploreService = inject(ExploreService);
  private readonly instantCheckupService = inject(InstantCheckupService);
  private readonly regimenService = inject(RegimenService);
  private readonly shopService = inject(ShopService);
  private readonly store = inject(Store);

  private readonly swiperConfigs = new Map<number, SwiperOptions>();
  private readonly subscriptions: Subscription[] = [];
  experiments: Experiment[];
  loading = signal<boolean>(true);
  productsInCart: { [key: string]: number } = {};
  numberOfProductsInCart: number = 0;
  isCheckoutStickyVisible: boolean = true;
  regimens: Regimen[] = [];
  products: Array<Catalog & { isLocked?: boolean }> = [];
  user: Table.User | null = null;
  userName: string = '';
  userPhoto: string = '';
  private cartItems: Array<{ productId: string; quantity: number }> = [];
  private isCartOperationPending: boolean = false;
  shopCollections: { [key: string]: ProductCollection } = {};
  readonly DisplayType = ProductCollectionDisplayTypeEnum;
  acqShopExperiment: AcqShopExperiment | null = null;
  activeRegimenClass: RegimenClass | undefined;
  lockedProductIds: string[] = [];
  availableProductIds: string[] = [];
  private readonly regimenProductMap = new Map<string, string>();
  showExploreTab: boolean = false;
  shopSectionOrder: string[] = [
    'nutra-orals',
    'combos',
    'filter-strip',
    'facewash',
    'sunscreen',
    'enhancers',
    'moisturiser',
    'faceSerum',
    'kits',
    'hair',
    'body',
    'lip',
    'skin-repair'
  ]

  get isBottomNavExperimentActive(): boolean {
    return this.experimentService.isBottomNavConfigActive();
  }

  constructor() {
    this.currentComponentService.set(this);
    this.subscriptions.push(
      this.store.select(fromSelectors.selectCartProducts).subscribe((products: { [key: string]: number }) => {
        this.productsInCart = { ...products };
        this.numberOfProductsInCart = Object.values(products).reduce((total: number, quantity: number) => {
          return total + (Number(quantity) || 0);
        }, 0);
      }),
      this.broadcast.on('REFRESH_SHOP_CART').subscribe(() => {
        this.loadCartData();
      }),
      this.exploreService.showExploreTab$.subscribe((showTab: boolean) => {
        this.showExploreTab = showTab;
      })
    );
  }

  async ngOnInit(): Promise<void> {
    this.user = await this.connectionService.getActingUser();
    this.userName = this.user?.get('PatientName')?.split(' ')[0] || '';
    await this.loadCartData();
    this.regimenService.invalidateRegimenCache();
    this.regimens = await this.regimenService.getRegimens();
    this.activeRegimenClass = this.resolveActiveRegimenClass(this.regimens);
    this.userPhoto = await this.resolveUserPhoto(this.user);
    this.acqShopExperiment = this.experimentService.getAcqShopExperiment();
    this.shopSectionOrder = this.acqShopExperiment?.variant?.sectionOrder[this.activeRegimenClass] || this.shopSectionOrder;
    this.products = this.shopService.getRegimenProducts(this.regimens);
    this.lockedProductIds = this.products.filter(product => product.isLocked).map(product => product.objectId);
    this.availableProductIds = this.products.filter(product => !this.lockedProductIds.includes(product.objectId)).map(product => product.objectId);
    this.experiments = await this.connectionService.findUserActiveExperiments();
    this.exploreService.handleExploreTabExperiment(
      this.experiments,
      this.user?.get('orderState')
    );
    this.buildRegimenProductMap();
    this.loading.set(false);

    await this.fetchProductCollections();
  }

  async fetchProductCollections(): Promise<void> {
    for (const collectionName of this.shopSectionOrder) {
      try {
        const productCollection = await this.connectionService.fetchProductCollection(collectionName);
        if (productCollection?.products?.length) {
          productCollection.products = this.sortProductsByUnlockedFirst(productCollection.products);
        }
        this.shopCollections[collectionName] = productCollection;
      } catch (error) {
        this.eventLoggerService.logError(error, false, 'fetchProductCollections');
      }
    }
  }

  private async resolveUserPhoto(user: Table.User | null): Promise<string> {
    if (!user?.id || !this.regimens.length) return '';

    const faceClass = this.appConfig.Shared.Regimen.Class.FACE;
    const hairClass = this.appConfig.Shared.Regimen.Class.HAIR;
    const primaryClass = this.activeRegimenClass === RegimenClass.Hair ? hairClass : faceClass;
    const primaryRegimen = this.regimens.find(r => r?.class === primaryClass) ?? this.regimens[0];

    const primary = await this.fetchInstantCheckupImage(user.id, primaryClass);
    if (primary) return primary;

    if (this.activeRegimenClass === RegimenClass.Both) {
      const fallback = await this.fetchInstantCheckupImage(user.id, hairClass);
      if (fallback) return fallback;
    }
    return primaryRegimen?.regimenCoverImage || '';
  }

  private fetchInstantCheckupImage(userId: string, regimenClass: string): Promise<string | undefined> {
    const regimen = this.regimens.find(r => r?.class === regimenClass) ?? this.regimens[0];
    return this.instantCheckupService.getUserInitialInstantCheckupImage(userId, regimen, regimenClass);
  }

  private resolveActiveRegimenClass(regimens: Regimen[]): RegimenClass | undefined {
    const faceClass = this.appConfig.Shared.Regimen.Class.FACE;
    const hairClass = this.appConfig.Shared.Regimen.Class.HAIR;
    const hasFace = regimens.some(r => r?.class === faceClass);
    const hasHair = regimens.some(r => r?.class === hairClass);

    if (hasFace && hasHair) return RegimenClass.Both;
    if (hasFace) return RegimenClass.Face;
    if (hasHair) return RegimenClass.Hair;
    return undefined;
  }

  private sortProductsByUnlockedFirst(products: Catalog[]): Catalog[] {
    const availableSet = new Set(this.availableProductIds);
    return [...products].sort((a, b) => {
      const aRank = availableSet.has(a.objectId) ? 0 : 1;
      const bRank = availableSet.has(b.objectId) ? 0 : 1;
      return aRank - bRank;
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  goToCart(): void {
    this.connectionService.navigateToURL('/cart');
  }

  onTabChange(index: number): void {
    switch (index) {
      case 1:
        this.router.navigate(['/support']);
        return;
      case 2:
        this.router.navigate(['/regimen'], { queryParams: { tab: 'regimen' } });
        return;
      case 3:
        this.router.navigate(['/explore'], { queryParams: { tab: 'explore' } });
        return;
      case 4:
        this.router.navigate(['/shop'], { queryParams: { tab: 'shop' } });
        return;
      case 0:
      default:
        this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
    }
  }

  cameraClickHome(): void {
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'home-camera' }));
    this.connectionService.navigateToURL('/user/instantCheckup');
  }

  shopClick(): void {
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'home-shop' }));
  }

  exploreClick(): void {
    this.eventLoggerService.cleverTapEvent('click-explore', JSON.stringify({ pageName: 'explore' }));
  }

  async onAddToCart(event: { product: Catalog; image: HTMLImageElement }): Promise<void> {
    await this.updateCart(event.product, this.appConfig.Shared.Cart.Action.ADD, event.image);
  }

  async onRemoveFromCart(product: Catalog): Promise<void> {
    await this.updateCart(product, this.appConfig.Shared.Cart.Action.REMOVE);
  }

  onBuyKit(regimen: Regimen): void {
    const productsCount = Number(extractRegimenProducts(regimen)?.length) || 0;
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({
      name: 'buy-complete-kit-banner',
      regimenClass: regimen?.class,
      fixedPrice: regimen?.fixedPrice,
      productsCount,
    }));
    this.router.navigate(['/user'], { queryParams: { tab: 'regimen', class: regimen?.class } });
  }

  private async updateCart(product: Catalog, action: string, image?: HTMLImageElement): Promise<void> {
    if (this.isCartOperationPending) return;
    this.isCartOperationPending = true;

    try {
      const options: CartUpdateOptions = {
        img: image,
        productsInCart: this.productsInCart,
        cartItems: this.cartItems,
        section: 'unpaidUserShop',
        skipGuardCheck: true,
        skipVariantsCheck: true,
      };
      const result = await this.cartService.addOrRemoveFromCart(product, action, options);

      if (result.success) {
        await this.loadCartData();
      }
    } finally {
      this.isCartOperationPending = false;
    }
  }

  private async loadCartData(): Promise<void> {
    const aovOptions = { aovTask: false, maxPrice: 0 };
    const cartData = await this.cartService.getCartAndPricingDetails(0, aovOptions);
    this.cartItems = cartData.cartItems ?? [];

    const cartProductQuantities: { [key: string]: number } = {};
    this.cartItems.forEach(item => {
      cartProductQuantities[item.productId] = item.quantity;
    });
    this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: cartProductQuantities }));
  }

  gotoProduct(product: Catalog, name: string): void {
    this.onLockedProductClick(product);
  }

  onLockedProductClick(product: Catalog): void {
    if (this.availableProductIds.includes(product.objectId)) return;

    const matchedRegimenClass = this.regimenProductMap.get(product.objectId);
    const type = matchedRegimenClass ? LockedProductType.Repair : LockedProductType.Unavailable;
    const regimenClass = matchedRegimenClass
      ?? this.regimens.find(r => r?.class === product?.category)?.class
      ?? this.regimens[0]?.class;

    if (!regimenClass) return;

    const pricing = this.shopService.getRegimenPricing(regimenClass, this.regimens);
    const productImage = getProductImage(product);

    this.eventLoggerService.cleverTapEvent('impression', JSON.stringify({
      name: 'locked-product-bottomsheet',
      type,
      regimenClass,
      productId: product?.objectId,
    }));

    this.bottomSheet.open(LockedProductBottomsheetComponent, {
      data: {
        type,
        productImage,
        regimenClass,
        ...pricing,
      } as LockedProductBottomsheetData,
    });
  }

  private buildRegimenProductMap(): void {
    for (const regimen of this.regimens) {
      const regimenProducts = extractRegimenProducts(regimen);
      for (const item of regimenProducts) {
        if (item.product?.objectId) {
          this.regimenProductMap.set(item.product.objectId, regimen.class);
        }
      }
    }
  }

  getDisplayType(name: string): ProductCollectionDisplayTypeEnum | undefined {
    return this.shopCollections[name]?.displayType;
  }

  getSwiperConfig(index: number, name: string): SwiperOptions {
    const cached = this.swiperConfigs.get(index);
    if (cached) return cached;

    const config: SwiperOptions = {
      slidesPerView: 1.17,
      pagination: {
        el: `#swiper-pagination-${index}`,
        clickable: true,
        bulletClass: 'tw-h-1 tw-w-1 tw-m-0 tw-bg-white tw-rounded-full',
        bulletActiveClass: 'tw-bg-black'
      },
      spaceBetween: 8,
      threshold: 5,
      loop: true,
      loopedSlides: this.shopCollections[name]?.products?.length || null,
    };
    this.swiperConfigs.set(index, config);
    return config;
  }
}
